import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, UsersRound } from "lucide-react";
import { useState } from "react";

import { api } from "@/api";
import {
  Button,
  EmptyState,
  ErrorState,
  money,
  Panel,
  SectionHeading,
  Skeleton,
} from "@/components/ui";
import { OwnershipSubnav } from "@/components/OwnershipSubnav";
import { useSession } from "@/session/SessionProvider";

type Syndicate = {
  syndicateId: string;
  series: string;
  hostName: string;
  status: string;
  priceCents: number;
  createdAt: string;
  combinedEntropyHash: string | null;
  slots: Array<{
    slotId: number;
    displayName: string;
    shareBp: number;
    priceCents: number;
    status: string;
  }>;
};

export default function SyndicatesPage() {
  const { session, requestLogin } = useSession();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState<{
    playerId: number;
    displayName: string;
  } | null>(null);
  const [seriesCode, setSeriesCode] = useState("");

  const mine = useQuery({
    queryKey: ["syndicates", "mine", session?.playerId],
    queryFn: () => api.mySyndicates() as Promise<{ syndicates: Syndicate[] }>,
    enabled: Boolean(session),
  });
  const series = useQuery({
    queryKey: ["packs", "series"],
    queryFn: api.packSeries,
  });
  const players = useQuery({
    queryKey: ["players", "search", search],
    queryFn: () =>
      api.searchPlayers(search) as Promise<{
        players: Array<{ playerId: number; displayName: string }>;
      }>,
    enabled: Boolean(session) && search.trim().length >= 2,
  });
  const create = useMutation({
    mutationFn: () =>
      api.openSyndicate({
        series: seriesCode || series.data!.series[0].code,
        splits: [
          { playerId: session!.playerId, shareBp: 5000 },
          { playerId: selectedPlayer!.playerId, shareBp: 5000 },
        ],
      }),
    onSuccess: () => {
      setSelectedPlayer(null);
      setSearch("");
      void queryClient.invalidateQueries({ queryKey: ["syndicates"] });
    },
  });
  const pay = useMutation({
    mutationFn: (syndicateId: string) =>
      api.paySyndicate(syndicateId, crypto.randomUUID()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["syndicates"] });
      void queryClient.invalidateQueries({ queryKey: ["balance"] });
      void queryClient.invalidateQueries({ queryKey: ["stable"] });
    },
  });

  if (!session) {
    return (
      <div className="page">
        <OwnershipSubnav />
        <Panel>
          <EmptyState
            action={<Button onClick={requestLogin}>Sign in</Button>}
            detail="Coordinate a group pack purchase while every resulting share remains player-held."
            title="Sign in to use syndicates"
          />
        </Panel>
      </div>
    );
  }

  return (
    <div className="page page--syndicates">
      <OwnershipSubnav />
      <SectionHeading
        icon={<UsersRound aria-hidden />}
        title="Group pack purchase"
      />
      <Panel className="syndicate-create">
        <div>
          <strong>Open a 50 / 50 room</strong>
          <span>
            Each participant pays their exact share and contributes entropy.
            Horses are co-owned directly by players, never by the room.
          </span>
        </div>
        {series.error ? (
          <div className="route-feedback">
            {series.data ? (
              <p className="form-note">Showing the last loaded pack series.</p>
            ) : null}
            <ErrorState
              error={series.error}
              retry={() => void series.refetch()}
            />
          </div>
        ) : null}
        {series.isPending ? (
          <div className="route-feedback">
            <Skeleton rows={1} />
          </div>
        ) : null}
        {series.data && !series.data.series.length ? (
          <div className="route-feedback">
            <EmptyState title="No pack series available" />
          </div>
        ) : null}
        <label>
          Pack series
          <select
            disabled={!series.data?.series.length}
            onChange={(event) => setSeriesCode(event.target.value)}
            value={seriesCode || series.data?.series[0]?.code || ""}
          >
            {series.data?.series.map((row) => (
              <option key={row.code} value={row.code}>
                {row.code} · {money(row.priceCents)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Invite stable
          <input
            onChange={(event) => {
              setSearch(event.target.value);
              setSelectedPlayer(null);
            }}
            placeholder="Search display name"
            value={selectedPlayer?.displayName ?? search}
          />
        </label>
        {search.trim().length >= 2 && !selectedPlayer ? (
          <div className="route-feedback">
            {players.error ? (
              <>
                {players.data ? (
                  <p className="form-note">
                    Showing the last loaded search results.
                  </p>
                ) : null}
                <ErrorState
                  error={players.error}
                  retry={() => void players.refetch()}
                />
              </>
            ) : null}
            {players.isPending ? <Skeleton rows={2} /> : null}
            {players.data && !players.data.players.length ? (
              <EmptyState title="No players found" />
            ) : null}
          </div>
        ) : null}
        {search.trim().length >= 2 &&
        players.data?.players.length &&
        !selectedPlayer ? (
          <div className="player-search-results">
            {players.data.players.map((player) => (
              <button
                key={player.playerId}
                onClick={() => setSelectedPlayer(player)}
                type="button"
              >
                {player.displayName}
              </button>
            ))}
          </div>
        ) : null}
        <Button
          disabled={
            !selectedPlayer || create.isPending || !series.data?.series.length
          }
          onClick={() => create.mutate()}
        >
          {create.isPending ? "Opening syndicate…" : "Open syndicate"}
        </Button>
        {create.error ? (
          <p className="form-error route-feedback" role="alert">
            Syndicate not confirmed: {create.error.message}
          </p>
        ) : null}
      </Panel>

      <SectionHeading icon={<KeyRound aria-hidden />} title="My syndicates" />
      {pay.error ? (
        <Panel className="route-feedback">
          <p className="form-error" role="alert">
            Slot payment not confirmed: {pay.error.message}
          </p>
        </Panel>
      ) : null}
      {mine.error ? (
        <Panel aria-label="My syndicates">
          {mine.data ? (
            <p className="form-note">Showing the last loaded syndicates.</p>
          ) : null}
          <ErrorState error={mine.error} retry={() => void mine.refetch()} />
        </Panel>
      ) : null}
      {mine.isPending ? (
        <Skeleton rows={3} variant="rooms" />
      ) : mine.data?.syndicates.length ? (
        <div className="syndicate-grid">
          {mine.data.syndicates.map((room) => (
            <Panel className="syndicate-card" key={room.syndicateId}>
              <header>
                <div>
                  <strong>{room.series}</strong>
                  <span>Hosted by {room.hostName}</span>
                </div>
                <b>{room.status}</b>
              </header>
              <div>
                {room.slots.map((slot) => (
                  <article key={slot.slotId}>
                    <span>{slot.displayName}</span>
                    <strong>{slot.shareBp / 100}%</strong>
                    <small>
                      {money(slot.priceCents)} · {slot.status}
                    </small>
                  </article>
                ))}
              </div>
              <footer>
                <span>
                  {room.combinedEntropyHash
                    ? `entropy ${room.combinedEntropyHash.slice(0, 12)}…`
                    : "entropy pending"}
                </span>
                {room.status === "open" ? (
                  <Button
                    disabled={pay.isPending}
                    onClick={() => pay.mutate(room.syndicateId)}
                  >
                    {pay.isPending && pay.variables === room.syndicateId
                      ? "Paying…"
                      : "Pay my slot"}
                  </Button>
                ) : null}
              </footer>
            </Panel>
          ))}
        </div>
      ) : mine.data ? (
        <EmptyState title="No syndicates yet" />
      ) : null}
    </div>
  );
}
